import { Collection, getFirebase } from 'firestorter';

import format from 'string-template';

import { firestorePaths, pathPlaceholder } from '../utils/paths';
import { IN_POD_STATUSES } from '../utils/coachPod';
import BaseDocument from './BaseDocument';
import User from './User';

const ChatSetting = {
  ENABLE_WORKOUT_NOTIFICATIONS: 'isWorkoutNotificationsEnabled',
  ENABLE_SUMMARY_NOTIFICATIONS: 'isSummaryNotificationsEnabled',
  ENABLE_FEEDBACK_NOTIFICATIONS: 'isFeedbackNotificationsEnabled',
};

const ISConfigOption = {
  IS_AUTOMATIC_MSG_ENABLED: 'automaticMsgEnabled',
  DISALLOWED_GENDERS: 'disallowedGenders',
  FINANCIAL_COMMITMENT_DISQUALIFICATION_ENABLED: 'shouldDisqualifyByFinancialCommitment',
  IS_AUTOMATIC_DISQUALIFICATION_EMAIL_ENABLED: 'isEnabled',
  CONTRACT_LINK: 'directPurchaseLink',
  POST_SUBMISSION_DIRECT_PURCHASE_OFFER_MESSAGE: 'postSubmissionDirectPurchaseOfferMessage',
  PAGE_LOAD_DIRECT_PURCHASE_OFFER_MESSAGE: 'pageLoadDirectPurchaseOfferMessage',
  MESSAGE: 'messageContent',
  SUBJECT: 'subject',
  ALLOWED_COUNTRIES: 'allowedCountries',
};

const DisallowedGender = {
  MALE: 'MALE',
  FEMALE: 'FEMALE',
  PREFER_NOT_TO_ANSWER: 'PREFER_NOT_TO_ANSWER',
  OTHER: 'OTHER',
};

const ColorModes = {
  LIGHT: 'LIGHT',
  DARK: 'DARK',
};

class Coach extends BaseDocument {
  get isSelfServeCoach() {
    return this.data.signupSource === 'self_serve';
  }

  get name() {
    return this.data.name;
  }

  get avatar() {
    return this.data.avatarUrl;
  }

  get picsUrls() {
    return this.data.picsUrls || [];
  }

  get photos() {
    return Coach.getAllPhotos(this.avatar, this.picsUrls);
  }

  static getAllPhotos(avatar, picsUrls) {
    return [
      avatar,
      ...picsUrls,
    ].filter((photo) => !!photo);
  }

  get summary() {
    return this.data.summary;
  }

  get bio() {
    return this.data.bio;
  }

  get certifications() {
    return this.data.certifications;
  }

  get focusAreas() {
    return this.data.focusAreas;
  }

  get location() {
    return this.data.location;
  }

  get interests() {
    return this.data.interests;
  }

  get isEligibleForCoachSelection() {
    return this.data.isEligibleForCoachSelection;
  }

  get currentLocation() {
    return this.data.location?.current;
  }

  get originalLocation() {
    return this.data.location?.original;
  }

  get testData() {
    return this.data.testData || {};
  }

  get assignmentWeight() {
    return this.data.assignmentWeight;
  }

  get gender() {
    return this.data.gender;
  }

  get isS2Coach() {
    return this.data.isS2Coach;
  }

  get mealPlanConfig() {
    return this.data.mealPlanConfig || {};
  }

  get mealPlanIntroMessage() {
    return this.data.mealPlanIntroMessage || '';
  }

  get termsAndConditionsText() {
    return this.data.termsAndConditionsText || '';
  }

  get feesConfig() {
    return this.data.feesConfig || {};
  }

  get coachAssistant() {
    return this.data.coachAssistant || {};
  }

  get feedbackGenerationConfig() {
    return this.data.feedbackGenerationConfig || {};
  }

  get notificationsConfig() {
    return this.data.notificationsConfig || {};
  }

  /**
   * Pushes the coach receives on their own phone, keyed by event (S2-9567). Unlike `notificationsConfig`,
   * which holds the pushes a coach sends to clients.
   */
  get coachPushNotificationsConfig() {
    return this.data.coachPushNotificationsConfig || {};
  }

  /**
   * The new-client push: off with no products until the coach turns it on and picks the products
   * that may send it. A push fires only when enabled and the product is in the list.
   * @return {{ enabled: boolean, productIds: string[] }}
   */
  get newClientPushConfig() {
    const { enabled = false, productIds = [], allProducts = false } = this.coachPushNotificationsConfig.newClient || {};
    return { enabled, productIds, allProducts };
  }

  get isEnrolledInISProgram() {
    return !!this.data.isEnrolledInISProgram;
  }

  get instaCallEnrolled() {
    return !!this.data.instaCallEnrolled;
  }

  get isACEnabled() {
    return !!this.data.isACEnabled;
  }

  /**
   * The coach's default native post payment form (forms doc id), used by any product that
   * does not pin one. Written via the setDefaultPostPaymentForm endpoint in normal operation;
   * re-validated at read time. Empty means the coach has no native PPF.
   * @returns {string} - The coach's default post payment form id, or ''.
   */
  get defaultPostPaymentFormId() {
    return this.data.defaultPostPaymentFormId || '';
  }

  get isAutomaticMsgEnabled() {
    return !!this.data.automaticMsgEnabled;
  }

  get leadEmailConfig() {
    return this.data.leadEmailConfig || {};
  }

  get isAutomaticEmailEnabled() {
    return !!this.leadEmailConfig.isEnabled;
  }

  get contractLink() {
    return this.leadEmailConfig.directPurchaseLink || '';
  }

  get disqualificationMessageContent() {
    return this.leadEmailConfig.messageContent || '';
  }

  get disqualificationMessageSubject() {
    return this.leadEmailConfig.subject || '';
  }

  get leadDisqualificationConfig() {
    return this.data.leadDisqualificationConfig || {};
  }

  get disallowedGenders() {
    return this.leadDisqualificationConfig.disallowedGenders || [];
  }

  get shouldDisqualifyByFinancialCommitment() {
    return !!this.leadDisqualificationConfig.shouldDisqualifyByFinancialCommitment;
  }

  get disqualifiedCountries() {
    return this.leadDisqualificationConfig?.countries?.disallowed || [];
  }

  get pageLoadDirectPurchaseOfferMessage() {
    return this.leadEmailConfig.pageLoadDirectPurchaseOfferMessage || '';
  }

  get postSubmissionDirectPurchaseOfferMessage() {
    return this.leadEmailConfig.postSubmissionDirectPurchaseOfferMessage || '';
  }

  get isSlackNotificationsEnabled() {
    return !!this.data.slackNotificationsEnabled;
  }

  get slackChannelId() {
    return this.data.slackChannelId || '';
  }

  get slackNotificationsConfig() {
    return this.data.slackNotificationsConfig || {};
  }

  get timeZone() {
    return this.data.timeZone;
  }

  get isActive() {
    return this.data.isActive;
  }

  get checkoutPageColorMode() {
    return this.data.checkoutPageColorMode || ColorModes.DARK;
  }

  get isAILeadDisqualificationEnabled() {
    return !!this.data.isAILeadDisqualificationEnabled;
  }

  /**
   * Whether the coach has enabled announcements.
   * This is used to hide the announcements page from the coach if they have not enabled it.
   * @returns {boolean}
   */
  get isAnnouncementsEnabled() {
    return !!this.data.isAnnouncementsEnabled;
  }

  get announcementNotificationsEnabled() {
    return !!this.data.announcementNotificationsEnabled;
  }

  get isWorkoutAssignmentClientNotificationEnabled() {
    return this.notificationsConfig.isWorkoutAssignmentClientNotificationEnabled ?? this.isACEnabled;
  }

  get isMealPlanAssignmentClientNotificationEnabled() {
    return this.notificationsConfig.isMealPlanAssignmentClientNotificationEnabled ?? this.isACEnabled;
  }

  get isEnrolledAt() {
    return this.data.isEnrolledAt;
  }

  get webPageUrl() {
    return this.data.webPageUrl || '';
  }

  get emojisSkinTone() {
    return this.data.emojisSkinTone || '';
  }

  get isHidden() {
    return !!this.data.isHidden;
  }

  get isPublicTracksCoach() {
    return !!this.data.isPublicTracksCoach;
  }

  get birthdayMsgConfig() {
    return this.data.birthdayMsgConfig || {};
  }

  setEmojisSkinTone(emojisSkinTone) {
    return this.updateFields({
      emojisSkinTone,
    });
  }

  setMealPlanIntroMessages({ mealPlanIntroMessage, challengeMealPlanIntroMessage }) {
    this.updateFields({
      mealPlanConfig: {
        ...this.mealPlanConfig,
        mealPlanIntroMessage,
        challengeMealPlanIntroMessage,
      },
    });
  }

  setFeesConfig(feesConfig, lastUpdatedBy) {
    this.updateFields({
      feesConfig,
      lastUpdatedByUserName: lastUpdatedBy,
    });
  }

  setCheckoutPageColorMode(checkoutPageColorMode) {
    return this.updateFields({
      checkoutPageColorMode,
    });
  }

  updateISEnrollment(isEnrolledInISProgram) {
    return this.updateFields({
      isEnrolledInISProgram,
      isEnrolledAt: isEnrolledInISProgram ? new Date() : getFirebase().firestore.FieldValue.delete(),
    });
  }

  setWebPageUrl(webPageUrl) {
    return this.updateFields({
      webPageUrl,
    });
  }

  getCoachInfo = (variant) => {
    const {
      id,
      name,
      bio,
      avatar,
      picsUrls,
      certifications,
      interests,
      currentLocation,
      originalLocation,
      summary,
      testData,
    } = this;

    let variantData = {};

    if (variant && testData[variant]) {
      variantData = testData[variant];
    }

    const coachInfo = {
      id,
      name,
      bio,
      avatar,
      picsUrls,
      certifications,
      interests,
      currentLocation,
      originalLocation,
      summary,
      ...variantData,
    };

    return {
      ...coachInfo,
      photos: Coach.getAllPhotos(coachInfo.avatar, coachInfo.picsUrls),
    };
  };

  async updateNotificationsConfig(setting, value) {
    await this.updateFields({
      notificationsConfig: {
        ...this.notificationsConfig,
        [setting]: value,
      },
    });
  }

  async updateAnnouncementNotificationSettings(value) {
    await this.updateFields({
      announcementNotificationsEnabled: value,
    });
  }

  async updateSlackNotificationsConfig(config) {
    await this.updateFields({
      slackNotificationsConfig: {
        ...this.slackNotificationsConfig,
        ...config,
      },
    });
  }

  async setWorkoutAssignmentClientNotificationEnabled(value) {
    await this.updateFields({
      notificationsConfig: {
        ...this.notificationsConfig,
        isWorkoutAssignmentClientNotificationEnabled: value,
      },
    });
  }

  /**
   * Saves the new-client push setting, recording who saved it: the coach, or an admin viewing as them.
   * @param {{ enabled: boolean, productIds: string[] }} config
   * @param {{ uid: string, name: string }} updatedBy
   */
  async setNewClientPushConfig({ enabled, productIds, allProducts }, { uid, name }) {
    await this.updateFields({
      coachPushNotificationsConfig: {
        ...this.coachPushNotificationsConfig,
        newClient: {
          enabled,
          productIds,
          allProducts,
          lastUpdatedBy: uid,
          lastUpdatedByUserName: name,
          lastUpdatedAt: new Date(),
        },
      },
    });
  }

  async setMealPlanAssignmentClientNotificationEnabled(value) {
    await this.updateFields({
      notificationsConfig: {
        ...this.notificationsConfig,
        isMealPlanAssignmentClientNotificationEnabled: value,
      },
    });
  }

  async setBirthdayMsgConfig({
    enabled,
    message,
    attachments,
    feedNotificationEnabled,
  }) {
    await this.updateFields({
      birthdayMsgConfig: {
        enabled,
        message: message || '',
        attachments: attachments || [],
        feedNotificationEnabled,
      },
    });
  }

  async updateInstantCallEnrolled(value) {
    return this.updateFields({
      instaCallEnrolled: value,
    });
  }

  static async getAllActiveCoaches() {
    const coachCollection = new Collection(firestorePaths.COACH_COLLECTION, {
      createDocument: (source, options) => new Coach(source, options),
      query: (query) => query
        .where('isActive', '==', true),
    });
    await Coach.initCollection(coachCollection);

    return coachCollection.hasDocs ? coachCollection.docs : [];
  }

  static async getAllCoaches() {
    const coachCollection = new Collection(firestorePaths.COACH_COLLECTION, {
      createDocument: (source, options) => new Coach(source, options),
    });
    await Coach.initCollection(coachCollection);

    return coachCollection.hasDocs ? coachCollection.docs : [];
  }

  static async getPublicTracksCoach() {
    const coachCollection = new Collection(firestorePaths.COACH_COLLECTION, {
      createDocument: (source, options) => new Coach(source, options),
      query: (query) => query
        .where('isPublicTracksCoach', '==', true)
        .limit(1),
    });
    await coachCollection.fetch();
    return coachCollection.hasDocs ? coachCollection.docs[0] : null;
  }

  static async getAllExternalCoaches() {
    const coachCollection = new Collection(firestorePaths.COACH_COLLECTION, {
      createDocument: (source, options) => new Coach(source, options),
      query: (query) => query
        .where('isS2Coach', '==', false)
        .where('isActive', '==', true),
    });
    await Coach.initCollection(coachCollection);

    return coachCollection;
  }

  /**
   * Fetches the given coach docs with batched documentId-in queries (10 ids per
   * query, the Firestore 'in' limit) instead of one read per coach.
   * @param {string[]} coachIds - Coach doc ids to fetch.
   * @returns {Promise<Coach[]>} The coaches that exist, in no particular order.
   */
  static async getCoachesByIds(coachIds) {
    const CHUNK_SIZE = 10;
    const chunks = [];
    for (let i = 0; i < coachIds.length; i += CHUNK_SIZE) {
      chunks.push(coachIds.slice(i, i + CHUNK_SIZE));
    }
    const collections = await Promise.all(chunks.map(async (chunk) => {
      const coachCollection = new Collection(firestorePaths.COACH_COLLECTION, {
        createDocument: (source, options) => new Coach(source, options),
        query: (query) => query.where(getFirebase().firestore.FieldPath.documentId(), 'in', chunk),
      });
      await Coach.initCollection(coachCollection);
      return coachCollection;
    }));
    return collections.flatMap((collection) => collection.docs);
  }

  static async getCoachById(coachId) {
    const coach = new Coach(format(firestorePaths.COACH_DOC, {
      [pathPlaceholder.COACH_ID]: coachId,
    }));
    await coach.init();
    return coach.exists ? coach : null;
  }

  static async isCoach(userId) {
    const coachDoc = new Coach(format(firestorePaths.COACH_DOC, {
      [pathPlaceholder.COACH_ID]: userId,
    }));

    await coachDoc.init();

    return coachDoc.exists;
  }

  static async getBroadcastMessageUsers(coachId) {
    const userCollection = new Collection(firestorePaths.USER,
      {
        // Dynamic map paths can't appear in composite indexes, so the
        // not-on-a-break condition moves in-memory (below).
        query: (ref) => ref
          .where(`coachData.${coachId}.flags.isActive`, '==', true),
        createDocument: (source, options) => new User(source, { ...options, viewAsCoachId: coachId }),
      });
    await User.initCollection(userCollection);
    if (!userCollection.hasDocs) {
      return [];
    }
    return userCollection.docs.filter((user) => !user.isOnBreakForAllProducts);
  }

  /**
   * The coach's clients whose subscription status keeps them in one of the coach's pods.
   * Paused clients are included — a break retains the pod assignment.
   * @param {string} coachId - The coach whose clients are fetched.
   * @return {Promise<Array<User>>} The in-pod client documents, scoped to this coach.
   */
  static async getInPodClients(coachId) {
    const userCollection = new Collection(firestorePaths.USER,
      {
        query: (ref) => ref
          .where(`coachData.${coachId}.flags.subscriptionStatus`, 'in', IN_POD_STATUSES),
        createDocument: (source, options) => new User(source, { ...options, viewAsCoachId: coachId }),
      });
    await User.initCollection(userCollection);
    return userCollection.hasDocs ? userCollection.docs : [];
  }

  /**
   * How many clients the coach has in a pod-eligible subscription state, paused clients included.
   * @param {string} coachId - The coach whose clients are counted.
   * @return {Promise<number>} The number of in-pod clients.
   */
  static async getInPodClientsCount(coachId) {
    const clients = await Coach.getInPodClients(coachId);
    return clients.length;
  }

  static async getAssistantCoaches() {
    const userCollection = new Collection(firestorePaths.USER,
      {
        query: (ref) => ref
          .where('isCoachAssistant', '==', true)
          .where('flags.isActive', '==', true),
        createDocument: (source, options) => new User(source, options),
      });
    await User.initCollection(userCollection);
    return userCollection;
  }
}

export default Coach;
export {
  ChatSetting,
  ISConfigOption,
  DisallowedGender,
  ColorModes,
};
